import { router, useGlobalSearchParams } from 'expo-router';
import { Platform, Pressable, View, StyleSheet, useWindowDimensions, Alert } from 'react-native';
import { Breakpoints, Color as ThemeColors, Padding } from '@/GlobalStyles';
import useFormStyle from '@/components/Styles/useFormStyle';
import { ActivityIndicator, Icon, IconButton, RadioButton, Text } from 'react-native-paper';
import HabitudesChart from '@/components/ScreenComponents/HabitudesChart';
import BaseContainer from '@/components/BaseContainer';
import Form from '@/components/Form/Form';
import ScreenMargin from '@/components/ScreenMargin';
import { useForm } from 'react-hook-form';
import { FormSection } from '@/components/Form/FormSectionContext';
import {
  Oeil,
  SubImplantsSubscription,
  useDeleteFavorisMutation,
  useGetParametresQuery,
  useInsertChoixMutation,
  useInsertFavorisMutation,
  useListLaboratoiresQuery,
  useSubImplantsSubscription,
  useUpdatePatientMutation,
} from '@/data/data';
import DropDownInput from '@/components/Form/DropDownInput';
import ImplantsChart from '@/components/ScreenComponents/ImplantsChart';
import ImplantsInfo from '@/components/ScreenComponents/ImplantsInfo';
import { useTranslation } from 'react-i18next';
import { usePatientContext } from '@/components/Contexts/PatientContext';
import Color from 'color';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import { useCallback, useState } from 'react';
import { useUserContext } from '@/components/Auth/UserContext';
import { useToast } from 'react-native-toast-notifications';
import PlusMinusInput from '@/components/Form/PlusMinusInput';
import CheckBoxInput from '@/components/Form/CheckBoxInput';
import IconListInput from '@/components/Form/IconListInput';
import FavoriteInput from '@/components/Form/FavoriteInput';
import RenderListItem from 'react-native-dropdown-picker/src/components/RenderListItem';
import Menu from '@/components/Menu/Menu';
import MenuDivider from '@/components/Menu/MenuDivider';
import { RenderListItemPropsInterface, ValueType } from 'react-native-dropdown-picker';
import { defocusAbsisse } from '@/data/visionData';
import Button from '@/components/Button';

type FormInputType = {
  choix: [
    {
      implantId: string;
      decalage?: number;
    },
    {
      implantId: string;
      decalage?: number;
    },
  ];
};

export const getImplantsList = (
  implants?: SubImplantsSubscription,
  type?: string,
  materiau?: string,
  laboratoireId?: string,
  t
) => {
  const parents: { [key: string]: { value: string; label: string } } = {};
  const children =
    (
      implants?.implant.map((implant) => {
        if (type && implant.type !== type) {
          return null;
        }
        if (materiau && implant.materiau !== materiau && materiau !== 'sans_pref') {
          return null;
        }
        if (laboratoireId && implant.laboratoire?.id !== laboratoireId) {
          return null;
        }
        if (implant.laboratoire) {
          parents[implant.laboratoire.id] = {
            value: implant.laboratoire.id,
            label: implant.laboratoire.nom || '',
          };
        }

        return {
          value: implant.id,
          label: implant.name,
          parent: implant.laboratoire.id,
          isFavorite: implant.favoris.length > 0,
        };
      }) || []
    ).filter((v) => v !== null) || [];

  const implantsList = [...children, ...Object.values(parents)];
  return implantsList;
};

export const getCourbePatient = (oeil: Oeil, show: boolean) => {
  if (!show) {
    return null;
  }
  if (oeil.genererCourbeTypeImplant && oeil.genererCourbeTypeImplant !== 'n/a') {
    if (oeil.genererCourbeTypeImplant === 'patient' && oeil.courbeDefocus) {
      const defocusOeilChartData = defocusAbsisse.map((value, k) => [
        value,
        oeil.courbeDefocus[k] !== undefined ? oeil.courbeDefocus[k] : null,
      ]);
      return defocusOeilChartData;
    } else if (oeil.genererCourbeTypeImplant === 'type') {
      let courbe: number[] | null = null;
      switch (oeil.typeImplantActuel) {
        case 'Monofocal':
          courbe = [0.6, 0.5, 0.2, 0.1, 0.02, 0.1, 0.3, 0.4, 0.5, 0.6, 0.75, 0.85, 0.9];
          break;
        case 'EDOF':
          courbe = [0.55, 0.41, 0.26, 0.12, -0.01, 0.04, 0.1, 0.2, 0.34, 0.48, 0.62, 0.72, 0.8];
          break;
        case 'MIOL':
          courbe = [0.62, 0.55, 0.28, 0.07, -0.1, 0.05, 0.1, 0.03, 0.01, 0.02, 0.1, 0.2, 0.37];
          break;
      }
      if (courbe !== null) {
        return defocusAbsisse.map((value, k) => [
          value,
          courbe !== null && courbe[k] !== undefined ? courbe[k] : null,
        ]);
      }
      return courbe;
    } else if (oeil.genererCourbeTypeImplant === 'implant') {
      const defocusOeilChartData = oeil.implantCourant?.data.map((value, k) => [
        value[0],
        value[1],
      ]);
      return defocusOeilChartData;
    }
  }
  return null;
};

const ImplantsPage = () => {
  const { detail } = useGlobalSearchParams();
  const { t, i18n } = useTranslation();
  const { data: info_gen } = useGetParametresQuery({
    variables: { keyList: ['INFO_GEN_' + i18n.language.toUpperCase()] },
  });

  const { patient } = usePatientContext();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < Breakpoints.tablet;
  // Onglet visible sur mobile : 0 = Choix 1 (oeil droit) / 1 = Choix 2 (oeil gauche)
  const [choixActifMobile, setChoixActifMobile] = useState<0 | 1>(0);

  const form = useForm();
  const { user } = useUserContext();
  const [insertFavoris] = useInsertFavorisMutation();
  const [deleteFavoris] = useDeleteFavorisMutation();
  const toast = useToast();

  const styles = useFormStyle();

  const [loading, setLoading] = useState(false);

  const { data: implants } = useSubImplantsSubscription();
  const { data: laboratoireList, loading: laboratoireListLoading } = useListLaboratoiresQuery();

  const [insertChoix] = useInsertChoixMutation();
  const [updatePatient] = useUpdatePatientMutation();

  const type0 = form.watch('choix.0.type');
  const materiau0 = form.watch('choix.0.materiau');
  const laboratoireId0 = form.watch('choix.0.laboratoireId');
  const implantsList0 = getImplantsList(implants, type0, materiau0, laboratoireId0, t);

  const type1 = form.watch('choix.1.type');
  const materiau1 = form.watch('choix.1.materiau');
  const laboratoireId1 = form.watch('choix.1.laboratoireId');
  const implantsList1 = getImplantsList(implants, type1, materiau1, laboratoireId1, t);

  const implantId0 = form.watch('choix.0.implantId');
  const implantId1 = form.watch('choix.1.implantId');

  const sameImplant = form.watch('sameImplant');

  const oeilGauche = patient?.oeils.find((oeil) => oeil.side === 'left');
  const oeilDroit = patient?.oeils.find((oeil) => oeil.side === 'right');

  const defaultValues = {
    choix: [
      {
        implantId: oeilGauche?.choixes[0]?.implantId,
        decalage: oeilGauche?.choixes[0]?.decalage || 0,
        type: oeilGauche?.choixes[0]?.type,
        materiau: oeilGauche?.choixes[0]?.materiau,
        affichageDefocus: oeilGauche?.choixes[0]?.affichageDefocus,
        decalageCourbeExistante: oeilGauche?.choixes[0]?.decalageCourbeExistante,
        laboratoireId: oeilGauche?.choixes[0]?.laboratoireId,
        final: oeilGauche?.choixes[0]?.final ? 1 : 0,
      },
      {
        implantId: oeilDroit?.choixes[0]?.implantId,
        decalage: oeilDroit?.choixes[0]?.decalage || 0,
        type: oeilDroit?.choixes[0]?.type,
        materiau: oeilDroit?.choixes[0]?.materiau,
        affichageDefocus: oeilDroit?.choixes[0]?.affichageDefocus,
        decalageCourbeExistante: oeilDroit?.choixes[0]?.decalageCourbeExistante,
        laboratoireId: oeilDroit?.choixes[0]?.laboratoireId,
        final: oeilDroit?.choixes[0]?.final ? 1 : 0,
      },
    ],
    lunettesVL: patient?.lunettesVL ? 1 : 0,
    lunettesVI: patient?.lunettesVI ? 1 : 0,
    lunettesVP: patient?.lunettesVP ? 1 : 0,
    sameImplant: patient?.sameImplant ? 1 : 0,
    monovision: patient?.monovision ? true : false,
  };

  const monovision = form.watch('monovision');
  const oeilDominant =
    form.watch('choix.0.decalage') > 0
      ? 'droit'
      : form.watch('choix.1.decalage') > 0
        ? 'gauche'
        : '';

  const renderListItem = useCallback(
    (
      props: RenderListItemPropsInterface<ValueType> & {
        item: RenderListItemPropsInterface<ValueType>['item'] & { isFavorite: boolean };
      }
    ) => {
      if (props.item.parent) {
        return (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <RenderListItem
              {...props}
              listItemContainerStyle={[props.listItemContainerStyle, { flexGrow: 1 }]}
            />
            <IconButton
              icon={props.item.isFavorite ? 'star' : 'star-outline'}
              size={18}
              iconColor={ThemeColors.sECJAUNE}
              onPress={async () => {
                if (props.item.isFavorite) {
                  await deleteFavoris({
                    fetchPolicy: 'no-cache',
                    variables: {
                      implantId: props.item.value,
                      chirurgienId: user?.id,
                    },
                  });
                } else {
                  await insertFavoris({
                    fetchPolicy: 'no-cache',
                    variables: {
                      object: {
                        implantId: props.item.value,
                        chirurgienId: user?.id,
                      },
                    },
                  });
                }
              }}
            />
          </View>
        );
      }
      return <RenderListItem {...props} />;
    },
    [insertFavoris, deleteFavoris, user]
  );

  return (
    <BaseContainer withScrollView={'keyboardAware'}>
      <ScreenMargin>
        <Form<FormInputType> {...form} defaultValues={defaultValues}>
          <View style={styles?.bodyForm}>
            <FormSection sectionOrder={1}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('Habitudes visuelles')}</Text>
                <View style={styles?.input1Col}>
                  <HabitudesChart language={i18n.language} />
                </View>
              </View>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('Informations générales')}</Text>
                <View style={styles?.input1Col}>
                  <Text>{info_gen?.parametres?.[0]?.paramValue}</Text>
                </View>
              </View>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t("Choix d'implants")}</Text>
                <View style={[styles.rowForm, { zIndex: 1 }]}>
                  <IconListInput
                    name={'monovision'}
                    label={'Je souhaite comparer des implants'}
                    withHelpButton={true}
                    helpFieldName="compareImplants"
                    data={[
                      {
                        value: false,
                        label: t('Oui'),
                      },
                      {
                        value: true,
                        label: t('Non'),
                      },
                    ]}
                    containerStyle={styles.input2Col}
                  />
                </View>
                {isMobile && !monovision ? (
                  <View style={style.tabBar}>
                    <Pressable
                      style={[
                        style.tabItem,
                        choixActifMobile === 0 && style.tabItemActive,
                      ]}
                      onPress={() => setChoixActifMobile(0)}
                    >
                      <Text
                        style={[
                          style.tabLabel,
                          choixActifMobile === 0 && style.tabLabelActive,
                        ]}
                      >
                        {t('Choix 1')}
                      </Text>
                    </Pressable>
                    <Pressable
                      style={[
                        style.tabItem,
                        choixActifMobile === 1 && style.tabItemActive,
                      ]}
                      onPress={() => setChoixActifMobile(1)}
                    >
                      <Text
                        style={[
                          style.tabLabel,
                          choixActifMobile === 1 && style.tabLabelActive,
                        ]}
                      >
                        {t('Choix 2')}
                      </Text>
                    </Pressable>
                  </View>
                ) : null}
                <View
                  style={[
                    styles?.input2Col,
                    Platform.OS === 'web' ? { zIndex: 'auto' } : {},
                    isMobile && !monovision && choixActifMobile !== 0
                      ? { display: 'none' }
                      : null,
                  ]}
                >
                  {!monovision ||
                  oeilDroit.aOperer == '1' ||
                  (oeilDroit.genererCourbeTypeImplant &&
                    oeilDroit.genererCourbeTypeImplant != 'n/a') ? (
                    <View
                      style={[
                        styles.colGroupForm,
                        styles.oeilContainer,
                        monovision
                          ? { backgroundColor: Color(ThemeColors.MainBleu).lighten(1.5).hex() }
                          : null,
                      ]}
                    >
                      <Text style={styles?.titleSubGroupCol}>
                        {monovision ? t('Oeil droit') : t('Choix 1')}
                      </Text>
                      {!monovision || oeilDroit.aOperer == '1' ? (
                        <>
                          <FavoriteInput
                            name={'choix.1.fav'}
                            label={'Implants favoris'}
                            withHelpButton={true}
                            helpFieldName="implantsFavoris"
                            data={
                              implants?.implant.filter(({ favoris }) => favoris.length > 0) || []
                            }
                            onChangeValue={(value) => {
                              form.setValue('choix.1.implantId', value);
                              //on cherche les infos de l'implant
                              const impSelect = implants?.implant.find(
                                (implant) => implant.id === value
                              );
                              if (impSelect) {
                                form.setValue('choix.1.laboratoireId', impSelect.laboratoire?.id);
                                form.setValue('choix.1.materiau', impSelect.materiau);
                                form.setValue('choix.1.type', impSelect.type);
                              }
                            }}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <IconListInput
                            name={'choix.1.type'}
                            label={'Type'}
                            withHelpButton={true}
                            helpFieldName="typeImplant"
                            containerStyle={{ marginTop: 20 }}
                            data={[
                              {
                                value: 'Monofocal',
                                label: t('Monofocal'),
                              },
                              {
                                value: 'EDOF',
                                label: t('EDOF'),
                              },
                              {
                                value: 'MIOL',
                                label: t('MIOL'),
                              },
                              oeilDroit?.ajoutAddon || (oeilGauche?.ajoutAddon && !monovision)
                                ? {
                                    value: 'Addon',
                                    label: t('Addon'),
                                  }
                                : null,
                            ].filter((item) => item !== null)}
                          />
                          <IconListInput
                            name={'choix.1.materiau'}
                            withHelpButton={true}
                            helpFieldName="materiauImplant"
                            containerStyle={{ marginTop: 20 }}
                            label={'Matériau'}
                            data={[
                              {
                                value: 'Hydrophobe',
                                label: t('Hydrophobe'),
                              },
                              {
                                value: 'Hydrophile',
                                label: t('Hydrophile'),
                              },
                              {
                                value: 'sans_pref',
                                label: t('Sans préférence'),
                              },
                            ]}
                          />
                          <DropDownInput
                            name={'choix.1.laboratoireId'}
                            label={'Laboratoire'}
                            withHelpButton={true}
                            helpFieldName="laboratoireImplant"
                            placeholder={t('Sélectionner un laboratoire')}
                            searchPlaceholder={t('Chercher un laboratoire')}
                            searchable={true}
                            items={laboratoireList?.laboratoire || []}
                            maxHeight={250}
                            zIndex={8000}
                            zIndexInverse={5000}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <DropDownInput
                            name={'choix.1.implantId'}
                            label={'Implant'}
                            withHelpButton={true}
                            helpFieldName="selectionImplant"
                            placeholder={t('Sélectionner un implant')}
                            searchPlaceholder={t('Chercher un implant')}
                            searchable={true}
                            renderListItem={renderListItem}
                            categorySelectable={false}
                            items={implantsList1 || []}
                            maxHeight={250}
                            zIndex={7000}
                            zIndexInverse={9000}
                            listParentLabelStyle={{ fontWeight: 'bold' }}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <ImplantsInfo
                            implantId={implantId1}
                            color={Color(ThemeColors.MainBleu).lighten(1.3).hex()}
                          />
                          {!monovision ? (
                            <Button
                              style={{ margin: Padding.p_xl }}
                              onPress={() => {
                                form.setValue('choix.0.implantId', implantId1);
                                form.setValue(
                                  'choix.0.laboratoireId',
                                  form.getValues('choix.1.laboratoireId')
                                );
                                form.setValue('choix.0.type', form.getValues('choix.1.type'));
                                form.setValue(
                                  'choix.0.materiau',
                                  form.getValues('choix.1.materiau')
                                );
                                form.setValue('monovision', true);
                              }}
                            >
                              {t('Choisir cet implant')}
                            </Button>
                          ) : (
                            <>
                              {form.watch('choix.0.decalage') > 0 ? (
                                <Text
                                  variant={'titleLarge'}
                                  style={{
                                    marginTop: 15,
                                    textAlign: 'center',
                                    height: 68,
                                    alignSelf: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  {t('Oeil dominant')}
                                </Text>
                              ) : (
                                <PlusMinusInput
                                  name={'choix.1.decalage'}
                                  label={'Décalage de la courbe de l’œil à opérer'}
                                  withHelpButton={true}
                                  helpFieldName="decalageCourbe"
                                  minimumValue={-2}
                                  maximumValue={4}
                                  step={0.25}
                                  unit={'δ'}
                                  beforeAdd={(currentValue, nextValue, callback) => {
                                    if (currentValue === 0) {
                                      Alert.alert(
                                        'Attention',
                                        "Confirmez-vous que l'oeil droit est l'oeil dominé ?",
                                        [
                                          {
                                            text: 'Annuler',
                                            style: 'cancel',
                                          },
                                          {
                                            text: 'Oui',
                                            onPress: () => {
                                              callback(nextValue);
                                            },
                                          },
                                        ]
                                      );
                                    } else {
                                      callback(nextValue);
                                    }
                                  }}
                                  containerStyle={{ marginTop: 15 }}
                                />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <Text variant={'titleMedium'} style={{ textAlign: 'center' }}>
                          {t("Cet oeil n'est pas à opérer")}
                        </Text>
                      )}
                      {oeilDroit?.genererCourbeTypeImplant &&
                      oeilDroit?.genererCourbeTypeImplant !== 'n/a' ? (
                        <Menu
                          isSimplePressable
                          topPosition={'center'}
                          position={'before'}
                          modalStyle={{
                            width: Math.min(windowWidth - 20, 400),
                            alignItems: 'center',
                          }}
                          anchorContainerStyle={style.optBtn}
                          anchor={
                            <View style={style.optBtn}>
                              <Text variant={'labelSmall'} style={{ color: ThemeColors.MainBleu }}>
                                {t('Options avancées')}
                              </Text>
                              <Icon
                                source={'dots-vertical'}
                                color={ThemeColors.MainBleu}
                                size={20}
                              />
                            </View>
                          }
                        >
                          <Text variant={'labelLarge'}>{t('Options avancées')}</Text>
                          <MenuDivider />
                          <CheckBoxInput
                            name={'choix.1.affichageDefocus'}
                            withHelpButton={true}
                            helpFieldName="affichageDefocus"
                            label={'Afficher la courbe de l’oeil déjà opéré'}
                          />
                          <PlusMinusInput
                            name={'choix.1.decalageCourbeExistante'}
                            withHelpButton={true}
                            helpFieldName="decalageCourbeExistante"
                            label={"Amétropie de l'oeil déjà opéré (SE)"}
                            minimumValue={-4}
                            maximumValue={2}
                            step={0.25}
                            unit={'δ'}
                            containerStyle={{ marginTop: 15 }}
                          />
                        </Menu>
                      ) : (
                        <View style={{ height: 20 }}></View>
                      )}
                    </View>
                  ) : null}
                </View>
                <View
                  style={[
                    styles?.input2Col,
                    Platform.OS === 'web' ? { zIndex: 'auto' } : {},
                    isMobile && !monovision && choixActifMobile !== 1
                      ? { display: 'none' }
                      : null,
                  ]}
                >
                  {!monovision ||
                  oeilGauche.aOperer == '1' ||
                  (oeilGauche.genererCourbeTypeImplant &&
                    oeilGauche.genererCourbeTypeImplant != 'n/a') ? (
                    <View
                      style={[
                        styles.colGroupForm,
                        styles.oeilContainer,
                        monovision
                          ? { backgroundColor: Color(ThemeColors.sECJAUNE).lighten(0.7).hex() }
                          : null,
                      ]}
                    >
                      <Text style={styles?.titleSubGroupCol}>
                        {monovision ? t('Oeil gauche') : t('Choix 2')}
                      </Text>
                      {!monovision || oeilGauche.aOperer == '1' ? (
                        <>
                          <FavoriteInput
                            name={'choix.0.fav'}
                            label={'Implants favoris'}
                            data={
                              implants?.implant.filter(({ favoris }) => favoris.length > 0) || []
                            }
                            containerStyle={{ marginTop: 20 }}
                            onChangeValue={(value) => {
                              form.setValue('choix.0.implantId', value);
                              //on cherche les infos de l'implant
                              const impSelect = implants?.implant.find(
                                (implant) => implant.id === value
                              );
                              if (impSelect) {
                                form.setValue('choix.0.laboratoireId', impSelect.laboratoire?.id);
                                form.setValue('choix.0.materiau', impSelect.materiau);
                                form.setValue('choix.0.type', impSelect.type);
                              }
                            }}
                          />
                          <IconListInput
                            name={'choix.0.type'}
                            label={'Type'}
                            withHelpButton={true}
                            helpFieldName="typeImplant"
                            data={[
                              {
                                value: 'Monofocal',
                                label: t('Monofocal'),
                              },
                              {
                                value: 'EDOF',
                                label: t('EDOF'),
                              },
                              {
                                value: 'MIOL',
                                label: t('MIOL'),
                              },
                              oeilGauche?.ajoutAddon || (oeilDroit?.ajoutAddon && !monovision)
                                ? {
                                    value: 'Addon',
                                    label: t('Addon'),
                                  }
                                : null,
                            ].filter((item) => item !== null)}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <IconListInput
                            name={'choix.0.materiau'}
                            withHelpButton={true}
                            helpFieldName="materiauImplant"
                            label={'Matériau'}
                            data={[
                              {
                                value: 'Hydrophobe',
                                label: t('Hydrophobe'),
                              },
                              {
                                value: 'Hydrophile',
                                label: t('Hydrophile'),
                              },
                              {
                                value: 'sans_pref',
                                label: t('Sans préférence'),
                              },
                            ]}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <DropDownInput
                            name={'choix.0.laboratoireId'}
                            label={'Laboratoire'}
                            withHelpButton={true}
                            helpFieldName="laboratoireImplant"
                            placeholder={t('Sélectionner un laboratoire')}
                            searchPlaceholder={t('Chercher un laboratoire')}
                            searchable={true}
                            items={laboratoireList?.laboratoire || []}
                            maxHeight={250}
                            zIndex={4000}
                            zIndexInverse={9000}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <DropDownInput
                            name={'choix.0.implantId'}
                            label={'Implant'}
                            withHelpButton={true}
                            helpFieldName="selectionImplant"
                            placeholder={t('Sélectionner un implant')}
                            searchPlaceholder={t('Chercher un implant')}
                            searchable={true}
                            renderListItem={renderListItem}
                            categorySelectable={false}
                            items={implantsList0 || []}
                            maxHeight={250}
                            zIndex={3000}
                            zIndexInverse={10000}
                            listParentLabelStyle={{ fontWeight: 'bold' }}
                            containerStyle={{ marginTop: 20 }}
                          />
                          <ImplantsInfo
                            implantId={implantId0}
                            color={Color(ThemeColors.sECJAUNE).lighten(0.35).hex()}
                          />
                          {!monovision ? (
                            <Button
                              style={{ margin: Padding.p_xl }}
                              onPress={() => {
                                form.setValue('choix.1.implantId', implantId0);
                                form.setValue(
                                  'choix.1.laboratoireId',
                                  form.getValues('choix.0.laboratoireId')
                                );
                                form.setValue('choix.1.type', form.getValues('choix.0.type'));
                                form.setValue(
                                  'choix.1.materiau',
                                  form.getValues('choix.0.materiau')
                                );
                                form.setValue('monovision', true);
                              }}
                            >
                              {t('Choisir cet implant')}
                            </Button>
                          ) : (
                            <>
                              {form.watch('choix.1.decalage') > 0 ? (
                                <Text
                                  variant={'titleLarge'}
                                  style={{
                                    marginTop: 15,
                                    textAlign: 'center',
                                    height: 68,
                                    alignSelf: 'center',
                                    alignContent: 'center',
                                  }}
                                >
                                  {t('Oeil dominant')}
                                </Text>
                              ) : (
                                <PlusMinusInput
                                  name={'choix.0.decalage'}
                                  label={'Décalage de la courbe de l’œil à opérer'}
                                  withHelpButton={true}
                                  helpFieldName="decalageCourbe"
                                  minimumValue={-2}
                                  maximumValue={4}
                                  step={0.25}
                                  unit={'δ'}
                                  beforeAdd={(currentValue, nextValue, callback) => {
                                    if (currentValue === 0) {
                                      Alert.alert(
                                        'Attention',
                                        "Confirmez-vous que l'oeil gauche est l'oeil dominé ?",
                                        [
                                          {
                                            text: 'Annuler',
                                            style: 'cancel',
                                          },
                                          {
                                            text: 'Oui',
                                            onPress: () => {
                                              callback(nextValue);
                                            },
                                          },
                                        ]
                                      );
                                    } else {
                                      callback(nextValue);
                                    }
                                  }}
                                  containerStyle={{ marginTop: 15 }}
                                />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <Text variant={'titleMedium'} style={{ textAlign: 'center' }}>
                          {t("Cet oeil n'est pas à opérer")}
                        </Text>
                      )}
                      {oeilGauche?.genererCourbeTypeImplant &&
                      oeilGauche?.genererCourbeTypeImplant !== 'n/a' ? (
                        <Menu
                          isSimplePressable
                          topPosition={'center'}
                          position={'before'}
                          modalStyle={{
                            width: Math.min(windowWidth - 20, 400),
                            alignItems: 'center',
                          }}
                          anchorContainerStyle={style.optBtn}
                          anchor={
                            <View style={style.optBtn}>
                              <Text variant={'labelSmall'} style={{ color: ThemeColors.MainBleu }}>
                                {t('Options avancées')}
                              </Text>
                              <Icon
                                source={'dots-vertical'}
                                color={ThemeColors.MainBleu}
                                size={20}
                              />
                            </View>
                          }
                        >
                          <Text variant={'labelLarge'}>{t('Options avancées')}</Text>
                          <MenuDivider />
                          <CheckBoxInput
                            name={'choix.0.affichageDefocus'}
                            withHelpButton={true}
                            helpFieldName="affichageDefocus"
                            label={'Afficher la courbe de l’oeil déjà opéré'}
                          />
                          <PlusMinusInput
                            name={'choix.0.decalageCourbeExistante'}
                            withHelpButton={true}
                            helpFieldName="decalageCourbeExistante"
                            label={"Amétropie de l'oeil déjà opéré (SE)"}
                            minimumValue={-4}
                            maximumValue={2}
                            step={0.25}
                            unit={'δ'}
                            containerStyle={{ marginTop: 15 }}
                          />
                        </Menu>
                      ) : (
                        <View style={{ height: 20 }}></View>
                      )}
                    </View>
                  ) : null}
                </View>
              </View>
              <View style={[styles?.input1Col, { flexBasis: 'auto', flexShrink: 0 }]}>
                <ImplantsChart
                  implants={{
                    0:
                      (form.watch('choix.0.final') === undefined &&
                        form.watch('choix.1.final') === undefined) ||
                      (form.watch('choix.0.final') === 0 && form.watch('choix.1.final') === 0) ||
                      form.watch('choix.0.final') === 1
                        ? implantId0
                        : undefined,
                    1:
                      (form.watch('choix.0.final') === undefined &&
                        form.watch('choix.1.final') === undefined) ||
                      (form.watch('choix.0.final') === 0 && form.watch('choix.1.final') === 0) ||
                      form.watch('choix.1.final') === 1
                        ? implantId1
                        : undefined,
                  }}
                  decalage={{
                    0: form.watch('choix.0.decalage', 0),
                    1: form.watch('choix.1.decalage', 0),
                  }}
                  courbePatient={{
                    OD: getCourbePatient(oeilDroit, form.watch('choix.1.affichageDefocus') === 1),
                    OG: getCourbePatient(oeilGauche, form.watch('choix.0.affichageDefocus') === 1),
                  }}
                  decalageCourbePatient={{
                    OD: -(form.watch('choix.1.decalageCourbeExistante', 0) || 0),
                    OG: -(form.watch('choix.0.decalageCourbeExistante', 0) || 0),
                  }}
                  oeilDominant={oeilDominant}
                  monovision={monovision}
                />
              </View>
              <View style={[styles?.input1Col, { flexBasis: 'auto', flexShrink: 0 }]}>
                <Text style={styles?.titleSubGroupCol}>
                  {t(
                    'Informer le patient que des lunettes seront sûrement nécessaires après l’intervention pour la'
                  ) + ' :'}
                </Text>
                <CheckBoxInput
                  name={'lunettesVL'}
                  label={'Vision de loin'}
                  withHelpButton={true}
                  helpFieldName="lunettesVL"
                />
                <CheckBoxInput
                  name={'lunettesVI'}
                  label={'Vision intermédiaire'}
                  withHelpButton={true}
                  helpFieldName="lunettesVI"
                />
                <CheckBoxInput
                  name={'lunettesVP'}
                  label={'Vision de près'}
                  withHelpButton={true}
                  helpFieldName="lunettesVP"
                />
              </View>
            </FormSection>
          </View>
          <View style={styles?.footerForm}>
            <FormSectionFooter
              saveLoading={loading}
              saveDisabled={
                loading ||
                (!monovision && !form.watch('choix.0.final') && !form.watch('choix.1.final'))
              }
              onPress={form.handleSubmit(async (data) => {
                setLoading(true);
                if (user) {
                  const oeilGaucheId = patient?.oeils?.find((oeil) => oeil.side === 'left');
                  const oeilDroitId = patient?.oeils?.find((oeil) => oeil.side === 'right');

                  const finalData: {
                    implantId?: string;
                    decalage?: number;
                    oeilId: string;
                    final?: boolean;
                    decalageCourbeExistante?: number;
                    affichageDefocus?: boolean;
                    type?: string;
                    materiau?: string;
                    laboratoireId?: string;
                  }[] = [];
                  const isMonoOG = data.monovision && oeilDominant !== 'gauche';
                  const isMonoOD = data.monovision && oeilDominant !== 'droit';
                  if (data.choix[0]?.implantId) {
                    finalData.push({
                      implantId: data.choix[0].implantId,
                      decalage: isMonoOG ? data.choix[0].decalage : 0,
                      decalageCourbeExistante: data.choix[0].decalageCourbeExistante,
                      affichageDefocus: data.choix[0].affichageDefocus,
                      type: data.choix[0].type,
                      materiau: data.choix[0].materiau,
                      laboratoireId: data.choix[0].laboratoireId,
                      final: data.monovision || data.choix[0].final === 1,
                      oeilId: oeilGaucheId?.id,
                    });
                  } else if (data.choix[0]?.affichageDefocus) {
                    finalData.push({
                      decalageCourbeExistante: data.choix[0].decalageCourbeExistante,
                      affichageDefocus: data.choix[0].affichageDefocus,
                      oeilId: oeilGaucheId?.id,
                    });
                  }
                  if (data.choix[1]?.implantId) {
                    finalData.push({
                      implantId: data.choix[1].implantId,
                      decalage: isMonoOD ? data.choix[1].decalage : 0,
                      decalageCourbeExistante: data.choix[1].decalageCourbeExistante,
                      affichageDefocus: data.choix[1].affichageDefocus,
                      type: data.choix[1].type,
                      materiau: data.choix[1].materiau,
                      laboratoireId: data.choix[1].laboratoireId,
                      final: data.monovision || data.choix[1].final === 1,
                      oeilId: oeilDroitId?.id,
                    });
                  } else if (data.choix[1]?.affichageDefocus) {
                    finalData.push({
                      decalageCourbeExistante: data.choix[1].decalageCourbeExistante,
                      affichageDefocus: data.choix[1].affichageDefocus,
                      oeilId: oeilDroitId?.id,
                    });
                  }

                  if (finalData.filter((v) => v.final || false).length === 0) {
                    toast.show(t('Vous devez choisir au moins un implant'), {
                      type: 'danger',
                      placement: 'center',
                      duration: 10000,
                      animationType: 'slide-in',
                      onPress: (id) => {
                        toast.hide(id);
                      },
                    });
                    setLoading(false);
                    return;
                  }
                  await insertChoix({
                    fetchPolicy: 'no-cache',
                    variables: {
                      objects: finalData,
                    },
                  });
                  await updatePatient({
                    fetchPolicy: 'no-cache',
                    variables: {
                      id: detail,
                      data: {
                        lunettesVI: data.lunettesVI === 1,
                        lunettesVP: data.lunettesVP === 1,
                        lunettesVL: data.lunettesVL === 1,
                        monovision: data.monovision,
                        oeilDominant: oeilDominant,
                      },
                    },
                  });
                  toast.show(t('Choix enregistrés'), {
                    type: 'success',
                    placement: 'center',
                    duration: 1000,
                    animationType: 'slide-in',
                    onPress: (id) => {
                      toast.hide(id);
                    },
                  });
                  router.push(`/patients/${detail}/rapport`);
                }
                setLoading(false);
              })}
            />
          </View>
        </Form>
      </ScreenMargin>
    </BaseContainer>
  );
};

export default ImplantsPage;

const style = StyleSheet.create({
  optBtn: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  tabBar: {
    flexDirection: 'row',
    width: '100%',
    marginTop: 8,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: ThemeColors.colorGray_100,
  },
  tabItem: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabItemActive: {
    borderBottomColor: ThemeColors.MainBleu,
  },
  tabLabel: {
    fontSize: 16,
    color: ThemeColors.MainGris,
    fontWeight: '500',
  },
  tabLabelActive: {
    color: ThemeColors.MainBleu,
    fontWeight: '700',
  },
});
